import {
  DynamicEvent,
  EmojiType,
  EmojiTypes,
  EventTypes,
  MeetingEmojiOffEvent,
  MeetingEmojiOnEvent,
  MeetingJoinEvent,
  MeetingLeaveEvent,
  UserCameraActivityEvent,
  UserMicActivityEvent,
} from 'nexgen-types';
import Swal from 'sweetalert2';
import { v4 as uuidv4 } from 'uuid';
import { EventService } from '../../../host/services/event.service';
import { BaseEventRouter } from '../../../shared/app/BaseEventRouter';
import { MeetingService } from '../services/MeetingService';
import { SocketService } from '../../../host/services/socket.service';
import { useWorkplaceStore } from '../../../host/stores';
import { useGuestStore } from '../../../host/stores/guest.store';
import { useMeetingStore } from '../../../host/stores/meeting.store';
import { useSessionStore } from '../../../host/stores/session.store';
import { leaveJitsiMeeting } from '../../../host/stores/utils/meeting-store-utils';
import { getDeviceId, DEVICE_KIND } from '../../../host/services/device-id.service';
import { isActivityTrackingEnabled } from '../../../host/services/activity-tracking-flag.util';

/**
 * Resolves the identity for activity-tracking events — an authenticated
 * user when present, otherwise falling back to the current guest visit's
 * pid (guests have no useWorkplaceStore user, but should still be tracked
 * for meeting/camera/mic activity same as an authenticated user). `guest_pid`
 * is only populated in the guest-fallback case, so real-user events don't
 * carry a stray guest_pid field.
 */
function resolveActivityIdentity(): { user_pid?: string; guest_pid?: string } {
  const user_pid = useWorkplaceStore.getState().user?.pid;
  if (user_pid) return { user_pid };

  const guest_pid = useGuestStore.getState().visitDetails?.guest?.pid;
  return { user_pid: guest_pid, guest_pid };
}

export class MeetingEventRouter extends BaseEventRouter {
  constructor(
    eventService: EventService,
    private meetingService: MeetingService
  ) {
    super(eventService, 'MeetingEventRouter');
  }

  registerEventListeners(): void {
    this.eventService.on(
      EventTypes.UserRoomChangedTopic,
      this.handleUserRoomChangeEvent.bind(this)
    );

    // Acks carry the payload on the event itself, not under `.data`: the topic path
    // unwraps `envelope.data.payload || envelope.data` before routing (SocketService), and
    // the event-ack path spreads the server payload onto `{ __type, ...data }`. Reading
    // `event.data` therefore handed the handlers `undefined` and threw on every ack the
    // emitter received for its own reaction ("Cannot destructure property 'user_pid'").
    // Keep the `.data` branch first in case a server ack ever nests its payload.
    this.eventService.on(EventTypes.MeetingEmojiOnAck, (event: any) =>
      this.meetingService.handleRemoteEmojiOn(event?.data ?? event)
    );

    this.eventService.on(EventTypes.MeetingEmojiOffAck, (event: any) =>
      this.meetingService.handleRemoteEmojiOff(event?.data ?? event)
    );

    this.eventService.on(
      EventTypes.MeetingEmojiOnTopic,
      this.meetingService.handleRemoteEmojiOn.bind(this)
    );

    this.eventService.on(
      EventTypes.MeetingEmojiOffTopic,
      this.meetingService.handleRemoteEmojiOff.bind(this)
    );

    this.eventService.on(
      EventTypes.MeetingLivecaptionsToggleTopic,
      this.meetingService.handleLiveCaptionsToggle.bind(this)
    );

    this.eventService.on(
      EventTypes.MeetingTranscriptionStreamTopic,
      this.meetingService.handleStreamingTranscription.bind(this)
    );

    // Listen for meeting join acknowledgment from server
    this.eventService.on('vo.meeting.join.ack', (data: any) => {
      if (data?.success) {
        this.onMeetingJoinAck();
      }
    });

    // Listen for forced-leave from backend (last-connection-wins eviction)
    this.eventService.on('vo.meeting.leave', this.handleForcedLeave.bind(this));
  }

  // Set while emitMeetingLeaveEvent is in flight so handleForcedLeave can
  // skip the local route (same __type as backend forced eviction).
  private isEmittingLocalLeave = false;

  private handleForcedLeave = async (): Promise<void> => {
    if (this.isEmittingLocalLeave) {
      return;
    }

    this._log('[LastConnectionWins] Session replaced — releasing meeting and prompting user');

    await leaveJitsiMeeting({ leaveReason: 'session_replaced' });

    try {
      SocketService.getInstance().destroy();
    } catch (err) {
      this._log('[LastConnectionWins] Failed to disconnect sockets:', err);
    }

    await Swal.fire({
      title: 'Session moved to another device',
      text: 'You started a new session elsewhere. Click OK to return to your room and continue session on this device.',
      icon: 'info',
      confirmButtonText: 'OK',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showLoaderOnConfirm: true,
      // Keep the popup open for 2s with a spinner on the button so 8x8 has
      // time to fully process the previous connection's disconnect before
      // we reload — otherwise the post-reload Jitsi join can race with 8x8's
      // slot cleanup, producing "Not connected" / session-invalid errors.
      preConfirm: async () => {
        await new Promise((resolve) => setTimeout(resolve, 2000));
      },
    });

    useSessionStore.getState().setSessionId(uuidv4());

    sessionStorage.setItem('force_home_room_on_init', '1');

    window.location.reload();
  };

  cleanup(): void {
    console.log('🧹 Cleaning up event listeners...');
    this.eventService.off('vo.meeting.leave', this.handleForcedLeave);
    this.eventService.off(
      EventTypes.UserRoomChangedTopic,
      this.handleUserRoomChangeEvent.bind(this)
    );

    // Cleanup live captions toggle event listener
    this.eventService.off(
      EventTypes.MeetingLivecaptionsToggleTopic,
      this.meetingService.handleLiveCaptionsToggle.bind(this)
    );

    this.eventService.off(
      EventTypes.MeetingTranscriptionStreamTopic,
      this.meetingService.handleStreamingTranscription.bind(this)
    );

    console.log('✅ Event listeners cleaned up');
  }

  emitRoomChangedEvent(payload: any) {
    this.eventService.emit(EventTypes.UserRoomChanged, payload, true);
  }

  /**
   * Emit Hand Raised Event
   */
  emitEmojiOnEvent(emojiType: EmojiType): void {
    try {
      const user_pid = useWorkplaceStore.getState().user?.pid;
      const currentRoomPid = useMeetingStore.getState().getCurrentRoomPid();

      const payload = new MeetingEmojiOnEvent({
        user_pid,
        meeting_pid: currentRoomPid,
        type: emojiType,
        timestamp: new Date().toISOString(),
        room_pid: currentRoomPid,
        // Sent for parity with every other meeting event (join/leave/camera activity): the
        // emoji events were the only ones omitting them, leaving server-side routing with
        // fewer fields to scope the broadcast by than the events around them.
        floor_pid: useWorkplaceStore.getState().currentRoom?.floor_pid,
        org_pid: useWorkplaceStore.getState().org?.pid,
      });

      this.eventService.emitTyped<MeetingEmojiOnEvent>(payload, true);
    } catch (error) {
      this._log('❌ Failed to emit Hand raised event:', error);
    }
  }

  emitEmojiOffEvent(emojiType: EmojiType): void {
    try {
      const user_pid = useWorkplaceStore.getState().user?.pid;
      const currentRoomPid = useMeetingStore.getState().getCurrentRoomPid();

      const payload = new MeetingEmojiOffEvent({
        user_pid,
        meeting_pid: currentRoomPid,
        type: emojiType,
        timestamp: new Date().toISOString(),
        room_pid: currentRoomPid,
        floor_pid: useWorkplaceStore.getState().currentRoom?.floor_pid,
        org_pid: useWorkplaceStore.getState().org?.pid,
      });

      this.eventService.emitTyped<MeetingEmojiOffEvent>(payload, true);
    } catch (error) {
      this._log('❌ Failed to emit Hand raised event:', error);
    }
  }

  private meetingJoinRetryCount = 0;
  private meetingJoinAckReceived = false;
  private meetingJoinRetryTimer: ReturnType<typeof setTimeout> | null = null;

  emitMeetingEntryEvent(isJoinEvent: boolean): void {
    if (isJoinEvent) {
      // Reset retry state for new join
      this.meetingJoinRetryCount = 0;
      this.meetingJoinAckReceived = false;

      // Clear any existing retry timer
      if (this.meetingJoinRetryTimer) {
        clearTimeout(this.meetingJoinRetryTimer);
        this.meetingJoinRetryTimer = null;
      }

      // Emit with retry mechanism
      this.emitMeetingJoinWithRetry();
    } else {
      // Leave event - no retry needed
      console.log('🚪 Emitting meeting leave event');
      this.emitMeetingLeaveEvent();
    }
  }

  private async emitMeetingJoinWithRetry(): Promise<void> {
    try {
      const { user_pid, guest_pid } = resolveActivityIdentity();
      const currentRoomPid = useMeetingStore.getState().getCurrentRoomPid();
      const org_pid = useWorkplaceStore.getState().org?.pid;
      const floor_pid = useWorkplaceStore.getState().currentRoom?.floor_pid;
      const session_id = useSessionStore.getState().sessionId ?? undefined;

      // NGE-841: org_pid/session_id/device_id/device_kind added so this event
      // carries the full NGE-836 payload contract for activity tracking —
      // previously only user_pid/meeting_pid/room_pid were set.
      const payload = {
        user_pid,
        guest_pid,
        meeting_pid: currentRoomPid,
        type: EmojiTypes[0],
        timestamp: new Date().toISOString(),
        room_pid: currentRoomPid,
        org_pid,
        floor_pid,
        session_id,
        device_id: getDeviceId(),
        device_kind: DEVICE_KIND,
      };

      // Add small delay on first attempt to ensure socket is stable after page refresh
      if (this.meetingJoinRetryCount === 0) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      this.eventService.emitTyped<MeetingJoinEvent>(new MeetingJoinEvent(payload), true);
      this.meetingJoinRetryCount++;

      // Set up retry if no ACK received within 3 seconds
      this.meetingJoinRetryTimer = setTimeout(() => {
        if (!this.meetingJoinAckReceived && this.meetingJoinRetryCount < 3) {
          this.emitMeetingJoinWithRetry();
        } else if (this.meetingJoinRetryCount >= 3 && !this.meetingJoinAckReceived) {
        }
      }, 3000);
    } catch (error) {
      console.log('❌ Failed to emit meeting join event:', error);
    }
  }

  private emitMeetingLeaveEvent(): void {
    this.isEmittingLocalLeave = true;
    try {
      const { user_pid, guest_pid } = resolveActivityIdentity();
      const currentRoomPid = useMeetingStore.getState().getCurrentRoomPid();
      const org_pid = useWorkplaceStore.getState().org?.pid;
      const floor_pid = useWorkplaceStore.getState().currentRoom?.floor_pid;
      const session_id = useSessionStore.getState().sessionId ?? undefined;

      // NGE-841: same payload-contract fix as emitMeetingJoinWithRetry above.
      const payload = {
        user_pid,
        guest_pid,
        meeting_pid: currentRoomPid,
        type: EmojiTypes[0],
        timestamp: new Date().toISOString(),
        room_pid: currentRoomPid,
        org_pid,
        floor_pid,
        session_id,
        device_id: getDeviceId(),
        device_kind: DEVICE_KIND,
      };

      this.eventService.emitTyped<MeetingLeaveEvent>(new MeetingLeaveEvent(payload), true);
    } catch (error) {
      console.log('❌ Failed to emit meeting leave event:', error);
    } finally {
      this.isEmittingLocalLeave = false;
    }
  }

  /**
   * Call this method when meeting join ACK is received
   */
  public onMeetingJoinAck(): void {
    this.meetingJoinAckReceived = true;
    if (this.meetingJoinRetryTimer) {
      clearTimeout(this.meetingJoinRetryTimer);
      this.meetingJoinRetryTimer = null;
    }
  }

  /**
   * Emit Camera Activity Event for tracking user camera on/off activity
   */
  emitCameraActivityEvent(status: 'on' | 'off'): void {
    // NGE-841: gated the same way emitMicActivityEvent() is — without this,
    // disabling VITE_ACTIVITY_TRACKING_ENABLED would still leave camera
    // activity flowing into the metrics pipeline while every other dimension
    // (idle/break/mic/clean-exit) correctly stopped.
    if (!isActivityTrackingEnabled()) return;

    try {
      const { user_pid, guest_pid } = resolveActivityIdentity();
      const currentRoomPid = useMeetingStore.getState().getCurrentRoomPid();
      const org_pid = useWorkplaceStore.getState().org?.pid;
      const floor_pid = useWorkplaceStore.getState().currentRoom?.floor_pid;

      const payload = new UserCameraActivityEvent({
        user_pid,
        guest_pid,
        status,
        org_pid,
        floor_pid,
        room_pid: currentRoomPid,
        timestamp: new Date().toISOString(),
      });

      this.eventService.emitTyped<UserCameraActivityEvent>(payload, true);
      this._log(`📹 Camera ${status} activity event emitted for user ${user_pid}`);
    } catch (error) {
      this._log('❌ Failed to emit camera activity event:', error);
    }
  }

  /**
   * Emit Mic Activity Event for tracking user microphone on/off activity
   * (NGE-841 — mirrors emitCameraActivityEvent, net-new per NGE-836/D5)
   */
  emitMicActivityEvent(status: 'on' | 'off'): void {
    if (!isActivityTrackingEnabled()) return;

    try {
      const { user_pid, guest_pid } = resolveActivityIdentity();
      if (!user_pid) return;

      const org_pid = useWorkplaceStore.getState().org?.pid;
      const session_id = useSessionStore.getState().sessionId ?? undefined;

      // Note: UserMicActivityEvent's payload has no room_pid field (unlike
      // camera) — only user_pid/status/org_pid/session_id/device_id/device_kind.
      const payload = new UserMicActivityEvent({
        user_pid,
        guest_pid,
        status,
        org_pid,
        session_id,
        device_id: getDeviceId(),
        device_kind: DEVICE_KIND,
      });

      this.eventService.emitTyped<UserMicActivityEvent>(payload, true);
      this._log(`🎙️ Mic ${status} activity event emitted for user ${user_pid}`);
    } catch (error) {
      this._log('❌ Failed to emit mic activity event:', error);
    }
  }

  /**
   * Emit Live Captions Toggle Event for testing purposes
   */
  emitLiveCaptionsToggleEvent(enabled: boolean, recordingInfo?: any): void {
    try {
      const user_pid = useWorkplaceStore.getState().user?.pid;
      const user = useWorkplaceStore.getState().user;
      const currentRoomPid = useMeetingStore.getState().getCurrentRoomPid();
      const org_pid = useWorkplaceStore.getState().org?.pid;
      const floor_pid = useWorkplaceStore.getState().currentRoom?.floor_pid || '';

      const payload = {
        type: EventTypes.MeetingLivecaptionsToggle,
        user_pid,
        room_pid: currentRoomPid,
        floor_pid, // Required by schema but not used for broadcasting
        org_pid,
        enabled,
        timestamp: new Date().toISOString(),
        recording_pid: recordingInfo?.recordingPid, // Include recording PID for standalone transcriptions
        // Include full recording info so participants don't need to fetch it
        recording_info: recordingInfo
          ? {
              recording_pid: recordingInfo.recordingPid,
              title: recordingInfo.title,
              recorded_at: recordingInfo.recordingStartTime,
              can_transcribe: true,
              type: recordingInfo.type,
              initiator_name: user?.name,
              initiator_avatar: user?.avatar_url,
            }
          : null,
      };

      this._log('MeetingLivecaptionsToggle', EventTypes.MeetingLivecaptionsToggle);
      this.eventService.emit(EventTypes.MeetingLivecaptionsToggle, payload, true);
      this._log(
        `📝 Live captions ${enabled ? 'enabled' : 'disabled'} event emitted for user ${user_pid}${recordingInfo?.recordingPid ? ` (recording: ${recordingInfo.recordingPid})` : ''}`
      );
    } catch (error) {
      this._log('❌ Failed to emit live captions toggle event:', error);
    }
  }

  /**
   * Emit Streaming Transcription Event to broadcast real-time transcripts
   */
  emitStreamingTranscriptionEvent(transcriptData: {
    event_id: string;
    user_pid: string;
    room_pid: string;
    floor_pid: string;
    org_pid: string;
    transcript_data: {
      id: string;
      text: string;
      confidence: number;
      isFinal: boolean;
      speaker?: string;
      timestamp: number;
    };
  }): void {
    try {
      this.eventService.emit(EventTypes.MeetingTranscriptionStream, transcriptData, true);
      this._log(
        `🎤 Streaming transcription event emitted: "${transcriptData.transcript_data.text.substring(0, 50)}..."`
      );
    } catch (error) {
      this._log('❌ Failed to emit streaming transcription event:', error);
    }
  }

  /**
   * Handle User Room Change Event
   */
  async handleUserRoomChangeEvent(event: DynamicEvent): Promise<void> {
    try {
      const { user_pid, floor_pid, room_pid }: any = event;
      this.meetingService.handleRoomChangeEvent(floor_pid, room_pid, user_pid);
    } catch (error) {
      console.log('❌ Error handling UserRoomChanged event:', error);
    }
  }

  unregisterEventListeners(): void {
    this.eventService.off(EventTypes.UserRoomChangedTopic, this.handleUserRoomChangeEvent);
  }
}
